/* eslint-disable no-template-curly-in-string */
import {
  ChatBubbleOvalLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  PlayCircleIcon,
  PlayIcon,
  PlusCircleIcon,
  ReceiptPercentIcon,
  StarIcon,
  StopCircleIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { StarIcon as OutlineStarIcon } from "@heroicons/react/24/outline";
import parse from "html-react-parser";
import lodash from "lodash";
import moment from "moment";
import { FC, useMemo } from "react";
import { classNames, displayUserName } from "utils";
import { Complaint } from "./schema";

export const actionsMap = {
  Start: "started working on ticket",
  Escalate: "escalated ticket to",
  ExtraWork: "completed resolution on Extra",
  ExtraWorkNo: "completed resolution on Extra No",
  HubtelWork:
    'started resolution on <span className="font-medium text-gray-900">Hubtel</span>',
  HubtelWorkDone:
    'completed resolution on <span className="font-medium text-gray-900">Hubtel</span>',
  AdoraWork:
    'started resolution on <span className="font-medium text-gray-900">Adora</span>',
  AdoraWorkDone:
    'completed resolution on<span className="font-medium text-gray-900">Adora</span>',
  ZeusWork:
    'initiated <span className="font-medium text-gray-900">${_.startCase(serviceOrderType)}</span> on ZEUS',
  ServiceOrder:
    'completed <span className="font-medium text-gray-900">${_.startCase(serviceOrderType)}</span> on ZEUS',
  Completed: "closed ticket",
  Resolved: "resolved ticket",
  ResolvedYes: "confirmed ticket as resolved",
  ResolvedNo: "marked ticket as not resolved",
  ResolvedUnreachable: "could not reach customer and closed ticket",
  Escalated: "escalated ticket to",
  Reassign: "reassigned ticket to",
  Returned: "returned ticket",
  Notes: "added a note",
};

const iconsMap = {
  Start: PlayCircleIcon,
  Escalate: UserCircleIcon,
  ExtraWork: PlayIcon,
  ExtraWorkNo: PlayIcon,
  HubtelWork: CurrencyDollarIcon,
  HubtelWorkDone: CurrencyDollarIcon,
  AdoraWork: ReceiptPercentIcon,
  AdoraWorkDone: ReceiptPercentIcon,
  ZeusWork: ClipboardDocumentListIcon,
  ServiceOrder: ClipboardDocumentCheckIcon,
  Completed: StopCircleIcon,
  Resolved: StopCircleIcon,
  ResolvedYes: StopCircleIcon,
  ResolvedNo: StopCircleIcon,
  ResolvedUnreachable: StopCircleIcon,
  Escalated: UserCircleIcon,
  Reassign: UserCircleIcon,
  Returned: UserCircleIcon,
  Notes: ChatBubbleOvalLeftEllipsisIcon,
  Feedback: StarIcon,
};

const iconBackgroundsMap = {
  Start: "bg-green-500",
  Escalate: "bg-gray-400",
  ExtraWork: "bg-red-500",
  ExtraWorkNo: "bg-red-500",
  HubtelWork: "bg-yellow-400",
  HubtelWorkDone: "bg-yellow-500",
  AdoraWork: "bg-red-400",
  AdoraWorkDone: "bg-red-500",
  ZeusWork: "bg-blue-400",
  ServiceOrder: "bg-blue-500",
  Completed: "bg-green-500",
  Resolved: "bg-emerald-500",
  ResolvedYes: "bg-emerald-600",
  ResolvedNo: "bg-orange-500",
  ResolvedUnreachable: "bg-slate-500",
  Escalated: "bg-gray-400",
  Reassign: "bg-indigo-500",
  Returned: "bg-rose-500",
  Notes: "bg-gray-500",
  Feedback: "bg-amber-500",
};

const MAX_STARS = 5;

function FeedbackStars({ rating }: { rating: number }) {
  const filled = Math.max(0, Math.min(MAX_STARS, Math.round(rating)));

  return (
    <div className="flex gap-1 items-center">
      {new Array(filled).fill(0).map((_, i) => (
        <StarIcon key={`solid-${i}`} className="h-4 w-4 text-yellow-500" />
      ))}
      {new Array(MAX_STARS - filled).fill(0).map((_, i) => (
        <OutlineStarIcon
          key={`outline-${i}`}
          className="h-4 w-4 text-yellow-500"
        />
      ))}
    </div>
  );
}

type TimelineStageItem = Complaint["stages"][number] & {
  kind: "stage";
  timestamp: string;
};

type TimelineFeedbackItem = {
  kind: "feedback";
  _id: string;
  timestamp: string;
  serviceStatus?: "Resolved" | "Unresolved";
  serviceRating?: number;
  serviceFeedback?: string;
};

type TimelineItem = TimelineStageItem | TimelineFeedbackItem;

interface TimelineContainerProps {
  complaint: Complaint;
}

const TimelineContainer: FC<TimelineContainerProps> = ({ complaint }) => {
  const timeline = useMemo(() => {
    const stages: TimelineStageItem[] = lodash
      .reject(complaint?.stages, ({ stage }) =>
        lodash.includes(["Escalate", "ExtraWork"], stage),
      )
      .map((stage) => ({
        ...stage,
        kind: "stage" as const,
      }));

    const feedbackEntries = [
      ...(complaint?.feedbackHistory || []),
      ...(complaint?.feedback?.status === "Submitted"
        ? [complaint.feedback]
        : []),
    ].filter((item) => item.submittedAt);

    const feedbackItems: TimelineFeedbackItem[] = feedbackEntries.map(
      (item, index) => ({
        kind: "feedback",
        _id: `feedback-${item.submittedAt}-${index}`,
        timestamp: item.submittedAt as string,
        serviceStatus: item.serviceStatus,
        serviceRating: item.serviceRating,
        serviceFeedback: item.serviceFeedback,
      }),
    );

    return [...stages, ...feedbackItems].sort(
      (a, b) => moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf(),
    ) as TimelineItem[];
  }, [complaint?.stages, complaint?.feedback, complaint?.feedbackHistory]);

  return (
    <section aria-labelledby="timeline-title">
      <div className="bg-white py-6 shadow sm:rounded-lg">
        <h2
          id="timeline-title"
          className="text-lg font-medium text-gray-900 px-6"
        >
          Timeline
        </h2>

        {/* Activity Feed */}
        <div className="mt-6 flow-root max-h-96 overflow-y-auto px-6">
          <ul className="-mb-8">
            <li>
              <div className="relative pb-8">
                {timeline.length ? (
                  <span
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div>
                    <span
                      className={classNames(
                        "bg-gray-400",
                        "flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white",
                      )}
                    >
                      <PlusCircleIcon
                        aria-hidden="true"
                        className="h-5 w-5 text-white"
                      />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                    <div>
                      <p className="text-sm text-gray-500">
                        Ticket was generated
                      </p>
                    </div>
                    <div className="whitespace-nowrap text-right text-sm text-gray-500">
                      <time dateTime={complaint?.createdAt}>
                        {moment(complaint?.createdAt).calendar({
                          lastDay: "[Yesterday at] hh:mm A",
                          sameDay: "[Today at] hh:mm A",
                          nextDay: "[Tomorrow at] hh:mm A",
                          lastWeek: "DD/MM/YYYY hh:mm A",
                          nextWeek: "dddd [at] hh:mm A",
                          sameElse: "DD/MM/YYYY hh:mm A",
                        })}
                      </time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            {timeline?.map((item, itemIdx) => {
              if (item.kind === "feedback") {
                const Icon = iconsMap.Feedback;
                return (
                  <li key={item._id}>
                    <div className="relative pb-8">
                      {itemIdx !== timeline.length - 1 ? (
                        <span
                          aria-hidden="true"
                          className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                        />
                      ) : null}
                      <div className="relative flex space-x-3">
                        <div>
                          <span
                            className={classNames(
                              iconBackgroundsMap.Feedback,
                              "flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white",
                            )}
                          >
                            <Icon
                              aria-hidden="true"
                              className="h-5 w-5 text-white"
                            />
                          </span>
                        </div>
                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                          <div>
                            <p className="text-sm text-gray-500">
                              <span className="font-medium text-gray-900">
                                Customer
                              </span>{" "}
                              submitted feedback
                            </p>
                            <div className="mt-1 space-y-1">
                              {item.serviceStatus && (
                                <p className="text-xs text-gray-500">
                                  Issue marked as{" "}
                                  {item.serviceStatus === "Resolved"
                                    ? "resolved"
                                    : "not resolved"}
                                </p>
                              )}
                              {typeof item.serviceRating === "number" && (
                                <FeedbackStars rating={item.serviceRating} />
                              )}
                              {item.serviceFeedback && (
                                <p className="text-xs text-gray-500 whitespace-pre-wrap">
                                  {item.serviceFeedback}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="whitespace-nowrap text-right text-sm text-gray-500">
                            <time dateTime={item.timestamp}>
                              {moment(item.timestamp).calendar({
                                lastDay: "[Yesterday at] hh:mm A",
                                sameDay: "[Today at] hh:mm A",
                                nextDay: "[Tomorrow at] hh:mm A",
                                lastWeek: "DD/MM/YYYY hh:mm A",
                                nextWeek: "dddd [at] hh:mm A",
                                sameElse: "DD/MM/YYYY hh:mm A",
                              })}
                            </time>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              }

              const Icon = iconsMap[item.stage];
              return (
                <li key={item._id}>
                  <div className="relative pb-8">
                    {itemIdx !== timeline.length - 1 ? (
                      <span
                        aria-hidden="true"
                        className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div>
                        <span
                          className={classNames(
                            iconBackgroundsMap[item.stage],
                            "flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white",
                          )}
                        >
                          <Icon
                            aria-hidden="true"
                            className="h-5 w-5 text-white"
                          />
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                        <div>
                          <p className="text-sm text-gray-500">
                            <span className="font-medium text-gray-900">
                              {item.stage === "Returned"
                                ? "Customer"
                                : displayUserName(item.user)}
                            </span>{" "}
                            {actionsMap[item.stage]
                              ? parse(
                                  lodash.template(actionsMap[item.stage])({
                                    ...item,
                                    _: lodash,
                                  }),
                                )
                              : null}{" "}
                            {item?.escalatedTo && (
                              <span className="font-medium text-gray-900">
                                {displayUserName(item.escalatedTo)}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="whitespace-nowrap text-right text-sm text-gray-500">
                          <time dateTime={item.timestamp}>
                            {moment(item.timestamp).calendar({
                              lastDay: "[Yesterday at] hh:mm A",
                              sameDay: "[Today at] hh:mm A",
                              nextDay: "[Tomorrow at] hh:mm A",
                              lastWeek: "DD/MM/YYYY hh:mm A",
                              nextWeek: "dddd [at] hh:mm A",
                              sameElse: "DD/MM/YYYY hh:mm A",
                            })}
                          </time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default TimelineContainer;
